import { useState, useEffect, useCallback, useRef } from 'react'
import { messageBelongsToContact } from '@/lib/whatsappJid'
import { logSendRejected, parseResponseAsJson } from '@/lib/fetchJsonResponse'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useConnectionVersion } from '@/hooks/useConnectionVersion'
import type { Database } from '@/lib/supabase'

type AccountMessage = Database['public']['Tables']['account_messages']['Row'] & {
  account_contact_id?: string | null
}
type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface OptimisticMessage extends Omit<AccountMessage, 'id'> {
  id: string | number
  isOptimistic?: boolean
  isSending?: boolean
  sendSuccess?: boolean
  sendError?: string
  processed_media_url?: string | null
}

interface UseMessagesPaginatedReturn {
  messages: OptimisticMessage[]
  loading: boolean
  error: string | null
  hasMore: boolean
  isLoadingMore: boolean
  loadMoreMessages: () => Promise<void>
  sendMessage: (contactJid: string, content: string) => Promise<boolean>
  sendAudio: (contactJid: string, audioBase64: string) => Promise<boolean>
  sendImage: (contactJid: string, imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>
  sendVideo: (contactJid: string, videoBase64: string, caption?: string, fileName?: string) => Promise<boolean>
  sendDocument: (contactJid: string, documentBase64: string, fileName: string, caption?: string) => Promise<boolean>
  refetch: () => Promise<void>
  sending: boolean
}

const INITIAL_PAGE_SIZE = 15
const PAGE_SIZE = 20
const RECENT_SYNC_LIMIT = 30
const RECENT_SYNC_INTERVAL_MS = 10000

export const useMessagesPaginated = (selectedContact: AccountContact | null): UseMessagesPaginatedReturn => {
  const { user } = useAuth()
  const { getUrl } = useConnectionVersion()
  const [messages, setMessages] = useState<OptimisticMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const oldestMessageRef = useRef<{ sent_at: string; id: string } | null>(null)
  const isInitialLoadRef = useRef(true)

  // Refs para prevenir race conditions
  const abortControllerRef = useRef<AbortController | null>(null)
  const fetchVersionRef = useRef(0)
  const expectedContactRef = useRef<{ id: string; jid: string } | null>(null)
  const currentContactRef = useRef<{ id: string; jid: string; lid?: string | null } | null>(null)

  const fetchInitialMessages = async (contactId: string): Promise<void> => {
    if (!selectedContact || !user?.account_id || !supabase) {
      return
    }

    // Cancelar fetch anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      console.log('⏸️ [useMessagesPaginated] Aborted previous fetch')
    }

    // Criar novo AbortController
    abortControllerRef.current = new AbortController()
    const currentController = abortControllerRef.current

    // Incrementar versão do fetch
    fetchVersionRef.current += 1
    const currentVersion = fetchVersionRef.current

    // Snapshot do contato esperado
    const expectedJid = selectedContact.jid
    expectedContactRef.current = { id: contactId, jid: expectedJid }

    try {
      // Loading e limpeza já foram feitos no useEffect de forma síncrona
      isInitialLoadRef.current = true

      console.log(`📥 [useMessagesPaginated] Fetching initial ${INITIAL_PAGE_SIZE} messages for contact ID: ${contactId} (v${currentVersion})`)

      const { data: messagesData, error: messagesError } = await supabase
        .from('account_messages')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('account_contact_id', contactId)
        .order('sent_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(INITIAL_PAGE_SIZE)
        .abortSignal(currentController.signal)

      if (messagesError) {
        // Verificar se foi abortado
        if (messagesError.message?.includes('aborted') || messagesError.message?.includes('cancelled')) {
          console.log('⏸️ [useMessagesPaginated] Fetch was aborted')
          return
        }
        throw messagesError
      }

      // VALIDAÇÃO CRÍTICA 1: Verificar se não foi abortado
      if (currentController.signal.aborted) {
        console.log('⏸️ [useMessagesPaginated] Fetch aborted after completion, ignoring results')
        return
      }

      // VALIDAÇÃO CRÍTICA 2: Verificar versão
      if (fetchVersionRef.current !== currentVersion) {
        console.log(`⚠️ [useMessagesPaginated] Stale fetch (v${currentVersion}), current is v${fetchVersionRef.current}. Ignoring results.`)
        return
      }

      // VALIDAÇÃO CRÍTICA 3: Verificar se contato ainda é o mesmo
      if (expectedContactRef.current?.id !== contactId || expectedContactRef.current?.jid !== expectedJid) {
        console.log(`⚠️ [useMessagesPaginated] Contact changed during fetch. Expected: ${contactId}/${expectedJid}, Got: ${expectedContactRef.current?.id}/${expectedContactRef.current?.jid}. Ignoring results.`)
        return
      }

      // VALIDAÇÃO CRÍTICA 4: Verificar selectedContact atual
      if (selectedContact.id !== contactId || selectedContact.jid !== expectedJid) {
        console.log(`⚠️ [useMessagesPaginated] selectedContact changed. Ignoring results.`)
        return
      }

      // Reverter para ordem cronológica (mais antiga primeiro)
      const sortedMessages = (messagesData || []).reverse()

      const dbMessages: OptimisticMessage[] = sortedMessages.map(msg => ({
        ...msg,
        isOptimistic: false
      }))

      console.log(`✅ [useMessagesPaginated] Loaded ${dbMessages.length} initial messages (v${currentVersion})`)

      // Atualizar referência da mensagem mais antiga
      if (sortedMessages.length > 0) {
        const oldest = sortedMessages[0]
        oldestMessageRef.current = { sent_at: oldest.sent_at, id: oldest.id }
      }

      // Verificar se há mais mensagens
      setHasMore(messagesData.length === INITIAL_PAGE_SIZE)

      setMessages(dbMessages)
      isInitialLoadRef.current = false
    } catch (err: any) {
      // Ignorar erros de abort
      if (err.name === 'AbortError' || err.message?.includes('aborted') || err.message?.includes('cancelled')) {
        console.log('⏸️ [useMessagesPaginated] Fetch aborted')
        return
      }

      // Só mostrar erro se ainda for o contato correto
      if (expectedContactRef.current?.id === contactId && selectedContact.id === contactId) {
        setError(err.message || 'Erro ao carregar mensagens')
        console.error('❌ [useMessagesPaginated] Error fetching messages:', err)
      }
    } finally {
      // Só atualizar loading se ainda for o contato correto
      if (expectedContactRef.current?.id === contactId && selectedContact.id === contactId) {
        setLoading(false)
      }
    }
  }

  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (!selectedContact || !user?.account_id || !supabase || !hasMore || isLoadingMore || loading) {
      return
    }

    if (!oldestMessageRef.current) {
      console.log('⚠️ [useMessagesPaginated] No oldest message reference, cannot load more')
      return
    }

    // Snapshot do contato antes da query
    const contactSnapshot = { id: selectedContact.id, jid: selectedContact.jid }

    try {
      setIsLoadingMore(true)
      console.log(`📥 [useMessagesPaginated] Loading ${PAGE_SIZE} more messages before:`, oldestMessageRef.current)

      const { data: olderMessages, error: olderError } = await supabase
        .from('account_messages')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('account_contact_id', contactSnapshot.id)
        .lt('sent_at', oldestMessageRef.current.sent_at)
        .order('sent_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(PAGE_SIZE)

      if (olderError) throw olderError

      // Validar se contato ainda é o mesmo
      if (selectedContact.id !== contactSnapshot.id || selectedContact.jid !== contactSnapshot.jid) {
        console.log('⚠️ [useMessagesPaginated] Contact changed during loadMore, ignoring results')
        return
      }

      if (!olderMessages || olderMessages.length === 0) {
        console.log('✅ [useMessagesPaginated] No more messages to load')
        setHasMore(false)
        return
      }

      // Reverter para ordem cronológica
      const sortedOlderMessages = olderMessages.reverse()

      const olderDbMessages: OptimisticMessage[] = sortedOlderMessages.map(msg => ({
        ...msg,
        isOptimistic: false
      }))

      console.log(`✅ [useMessagesPaginated] Loaded ${olderDbMessages.length} older messages`)

      // Atualizar referência da mensagem mais antiga
      if (sortedOlderMessages.length > 0) {
        const oldest = sortedOlderMessages[0]
        oldestMessageRef.current = { sent_at: oldest.sent_at, id: oldest.id }
      }

      // Verificar se há mais mensagens
      setHasMore(olderMessages.length === PAGE_SIZE)

      // Adicionar mensagens antigas no início do array
      setMessages(prev => [...olderDbMessages, ...prev])
    } catch (err: any) {
      console.error('❌ [useMessagesPaginated] Error loading more messages:', err)
      setError(err.message || 'Erro ao carregar mais mensagens')
    } finally {
      setIsLoadingMore(false)
    }
  }, [selectedContact, user?.account_id, hasMore, isLoadingMore, loading])

  const sendMessage = async (contactJid: string, content: string): Promise<boolean> => {
    if (!user || !content.trim()) {
      return false
    }

    const optimisticId = `optimistic_${Date.now()}_${Math.random()}`
    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      account_id: user.account_id,
      account_contact_id: selectedContact?.id ?? null,
      remote_jid: selectedContact?.jid || contactJid,
      from_jid: `${user.account_id}@s.whatsapp.net`,
      from_me: true,
      is_from_ai: false,
      wa_message_id: optimisticId,
      msg_type: 'conversation',
      text_content: content,
      status: 'PENDING',
      sent_at: new Date().toISOString(),
      received_at: new Date().toISOString(),
      media_kind: null,
      media_url: null,
      raw_payload: {},
      isOptimistic: true,
      isSending: true
    }

    setMessages(prev => [...prev, optimisticMessage])
    setSending(true)
    setError(null)

    try {
      if (!supabase) throw new Error('Supabase não configurado')

      const response = await fetch(getUrl('send-message'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: user.account_id,
          account_contact_id: selectedContact?.id,
          remote_jid: contactJid,
          text: content,
          delay: 0,
          linkPreview: true,
          mentionsEveryOne: false,
          mentioned: []
        })
      })

      const { json: result, rawText } = await parseResponseAsJson(response)
      if (!response.ok || result.success !== true) {
        logSendRejected('sendMessage', response, result, rawText)
        throw new Error((result.error as string) || `HTTP ${response.status}`)
      }

      console.log('✅ [sendMessage] Message sent successfully, converting to permanent')

      const responseData = (result.data || {}) as Record<string, unknown>
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? {
              ...msg,
              wa_message_id: (responseData.message_id as string) || msg.wa_message_id,
              status: (responseData.status as string) || 'SENT',
              sent_at: (responseData.sent_at as string) || msg.sent_at,
              isOptimistic: false,
              isSending: false,
              sendSuccess: true
            }
          : msg
      ))

      return true

    } catch (err: any) {
      console.error('❌ [sendMessage] Exceção:', err)
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? { ...msg, isSending: false, sendError: err.message || 'Erro ao enviar' }
          : msg
      ))
      setError(err.message || 'Erro ao enviar mensagem')
      return false
    } finally {
      setSending(false)
    }
  }

  const sendAudio = async (contactJid: string, audioBase64: string): Promise<boolean> => {
    if (!user || !audioBase64) {
      return false
    }

    const optimisticId = `optimistic_audio_${Date.now()}_${Math.random()}`
    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      account_id: user.account_id,
      account_contact_id: selectedContact?.id ?? null,
      remote_jid: selectedContact?.jid || contactJid,
      from_jid: `${user.account_id}@s.whatsapp.net`,
      from_me: true,
      is_from_ai: false,
      wa_message_id: optimisticId,
      msg_type: 'audioMessage',
      text_content: null,
      status: 'PENDING',
      sent_at: new Date().toISOString(),
      received_at: new Date().toISOString(),
      media_kind: 'audio',
      media_url: audioBase64,
      processed_media_url: audioBase64,
      raw_payload: {},
      isOptimistic: true,
      isSending: true
    }

    setMessages(prev => [...prev, optimisticMessage])
    setSending(true)
    setError(null)

    try {
      if (!supabase) throw new Error('Supabase não configurado')

      const response = await fetch(getUrl('send-audio'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: user.account_id,
          account_contact_id: selectedContact?.id,
          remote_jid: contactJid,
          audio: audioBase64,
          delay: 0
        })
      })

      const { json: result, rawText } = await parseResponseAsJson(response)
      if (!response.ok || result.success !== true) {
        logSendRejected('sendAudio', response, result, rawText)
        throw new Error((result.error as string) || `HTTP ${response.status}`)
      }

      console.log('✅ [sendAudio] Audio sent successfully, converting to permanent')

      const responseData = (result.data || {}) as Record<string, unknown>
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? {
              ...msg,
              wa_message_id: (responseData.message_id as string) || msg.wa_message_id,
              status: (responseData.status as string) || 'SENT',
              sent_at: (responseData.sent_at as string) || msg.sent_at,
              media_url: (responseData.audio_url as string) || msg.media_url,
              processed_media_url: (responseData.audio_url as string) || msg.processed_media_url,
              isOptimistic: false,
              isSending: false,
              sendSuccess: true
            }
          : msg
      ))

      return true

    } catch (err: any) {
      console.error('❌ [sendAudio] Exceção:', err)
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? { ...msg, isSending: false, sendError: err.message || 'Erro ao enviar áudio' }
          : msg
      ))
      setError(err.message || 'Erro ao enviar áudio')
      return false
    } finally {
      setSending(false)
    }
  }

  const sendImage = async (contactJid: string, imageBase64: string, caption?: string, fileName?: string): Promise<boolean> => {
    if (!user || !imageBase64) {
      return false
    }

    const optimisticId = `optimistic_image_${Date.now()}_${Math.random()}`
    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      account_id: user.account_id,
      account_contact_id: selectedContact?.id ?? null,
      remote_jid: selectedContact?.jid || contactJid,
      from_jid: `${user.account_id}@s.whatsapp.net`,
      from_me: true,
      is_from_ai: false,
      wa_message_id: optimisticId,
      msg_type: 'imageMessage',
      text_content: caption || null,
      status: 'PENDING',
      sent_at: new Date().toISOString(),
      received_at: new Date().toISOString(),
      media_kind: 'image',
      media_url: imageBase64,
      processed_media_url: imageBase64,
      raw_payload: {},
      isOptimistic: true,
      isSending: true
    }

    setMessages(prev => [...prev, optimisticMessage])
    setSending(true)
    setError(null)

    try {
      if (!supabase) throw new Error('Supabase não configurado')

      const response = await fetch(getUrl('send-image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: user.account_id,
          account_contact_id: selectedContact?.id,
          remote_jid: contactJid,
          media: imageBase64,
          caption: caption || '',
          fileName: fileName || 'image.jpg',
          delay: 0
        })
      })

      const { json: result, rawText } = await parseResponseAsJson(response)
      if (!response.ok || result.success !== true) {
        logSendRejected('sendImage', response, result, rawText)
        throw new Error((result.error as string) || `HTTP ${response.status}`)
      }

      console.log('✅ [sendImage] Image sent successfully, converting to permanent')

      const responseData = (result.data || {}) as Record<string, unknown>
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? {
              ...msg,
              wa_message_id: (responseData.message_id as string) || msg.wa_message_id,
              status: (responseData.status as string) || 'SENT',
              sent_at: (responseData.sent_at as string) || msg.sent_at,
              media_url: (responseData.media_url as string) || msg.media_url,
              processed_media_url: (responseData.media_url as string) || msg.processed_media_url,
              isOptimistic: false,
              isSending: false,
              sendSuccess: true
            }
          : msg
      ))

      return true

    } catch (err: any) {
      console.error('❌ [sendImage] Exceção:', err)
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? { ...msg, isSending: false, sendError: err.message || 'Erro ao enviar imagem' }
          : msg
      ))
      setError(err.message || 'Erro ao enviar imagem')
      return false
    } finally {
      setSending(false)
    }
  }

  const sendVideo = async (contactJid: string, videoBase64: string, caption?: string, fileName?: string): Promise<boolean> => {
    if (!user || !videoBase64) {
      return false
    }

    const optimisticId = `optimistic_video_${Date.now()}_${Math.random()}`
    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      account_id: user.account_id,
      account_contact_id: selectedContact?.id ?? null,
      remote_jid: selectedContact?.jid || contactJid,
      from_jid: `${user.account_id}@s.whatsapp.net`,
      from_me: true,
      is_from_ai: false,
      wa_message_id: optimisticId,
      msg_type: 'videoMessage',
      text_content: caption || null,
      status: 'PENDING',
      sent_at: new Date().toISOString(),
      received_at: new Date().toISOString(),
      media_kind: 'video',
      media_url: videoBase64,
      processed_media_url: videoBase64,
      raw_payload: {},
      isOptimistic: true,
      isSending: true
    }

    setMessages(prev => [...prev, optimisticMessage])
    setSending(true)
    setError(null)

    try {
      if (!supabase) throw new Error('Supabase não configurado')

      const response = await fetch(getUrl('send-video'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: user.account_id,
          account_contact_id: selectedContact?.id,
          remote_jid: contactJid,
          media: videoBase64,
          caption: caption || '',
          fileName: fileName || 'video.mp4',
          delay: 0
        })
      })

      const { json: result, rawText } = await parseResponseAsJson(response)
      if (!response.ok || result.success !== true) {
        logSendRejected('sendVideo', response, result, rawText)
        throw new Error((result.error as string) || `HTTP ${response.status}`)
      }

      console.log('✅ [sendVideo] Video sent successfully, converting to permanent')

      const responseData = (result.data || {}) as Record<string, unknown>
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? {
              ...msg,
              wa_message_id: (responseData.message_id as string) || msg.wa_message_id,
              status: (responseData.status as string) || 'SENT',
              sent_at: (responseData.sent_at as string) || msg.sent_at,
              media_url: (responseData.media_url as string) || msg.media_url,
              processed_media_url: (responseData.media_url as string) || msg.processed_media_url,
              isOptimistic: false,
              isSending: false,
              sendSuccess: true
            }
          : msg
      ))

      return true

    } catch (err: any) {
      console.error('❌ [sendVideo] Exceção:', err)
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? { ...msg, isSending: false, sendError: err.message || 'Erro ao enviar vídeo' }
          : msg
      ))
      setError(err.message || 'Erro ao enviar vídeo')
      return false
    } finally {
      setSending(false)
    }
  }

  const sendDocument = async (contactJid: string, documentBase64: string, fileName: string, caption?: string): Promise<boolean> => {
    if (!user || !documentBase64 || !fileName) {
      return false
    }

    const optimisticId = `optimistic_document_${Date.now()}_${Math.random()}`
    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      account_id: user.account_id,
      account_contact_id: selectedContact?.id ?? null,
      remote_jid: selectedContact?.jid || contactJid,
      from_jid: `${user.account_id}@s.whatsapp.net`,
      from_me: true,
      is_from_ai: false,
      wa_message_id: optimisticId,
      msg_type: 'documentMessage',
      text_content: caption || null,
      status: 'PENDING',
      sent_at: new Date().toISOString(),
      received_at: new Date().toISOString(),
      media_kind: 'document',
      media_url: null,
      processed_media_url: null,
      raw_payload: {
        fileName: fileName,
        sending: true
      },
      isOptimistic: true,
      isSending: true
    }

    setMessages(prev => [...prev, optimisticMessage])
    setSending(true)
    setError(null)

    try {
      if (!supabase) throw new Error('Supabase não configurado')

      const response = await fetch(getUrl('send-document'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: user.account_id,
          account_contact_id: selectedContact?.id,
          remote_jid: contactJid,
          media: documentBase64,
          fileName: fileName,
          caption: caption || '',
          delay: 0
        })
      })

      const { json: result, rawText } = await parseResponseAsJson(response)
      if (!response.ok || result.success !== true) {
        logSendRejected('sendDocument', response, result, rawText)
        throw new Error((result.error as string) || `HTTP ${response.status}`)
      }

      console.log('✅ [sendDocument] Document sent successfully, converting to permanent')

      const responseData = (result.data || {}) as Record<string, unknown>
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? {
              ...msg,
              wa_message_id: (responseData.message_id as string) || msg.wa_message_id,
              status: (responseData.status as string) || 'SENT',
              sent_at: (responseData.sent_at as string) || msg.sent_at,
              media_url: (responseData.media_url as string) || msg.media_url,
              processed_media_url: (responseData.media_url as string) || msg.processed_media_url,
              raw_payload: {
                ...((msg.raw_payload as Record<string, unknown>) || {}),
                fileName: responseData.fileName || (msg.raw_payload as Record<string, unknown>)?.fileName
              },
              isOptimistic: false,
              isSending: false,
              sendSuccess: true
            }
          : msg
      ))

      return true

    } catch (err: any) {
      console.error('❌ [sendDocument] Exceção:', err)
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticId
          ? { ...msg, isSending: false, sendError: err.message || 'Erro ao enviar documento' }
          : msg
      ))
      setError(err.message || 'Erro ao enviar documento')
      return false
    } finally {
      setSending(false)
    }
  }

  const refetch = async (): Promise<void> => {
    if (selectedContact) {
      await fetchInitialMessages(selectedContact.id)
    }
  }

  // Mantém o JID do contato atual sincronizado (migração @c.us/@s.whatsapp.net, LID, etc.)
  useEffect(() => {
    if (selectedContact) {
      currentContactRef.current = {
        id: selectedContact.id,
        jid: selectedContact.jid,
        lid: selectedContact.lid,
      }
    }
  }, [selectedContact?.id, selectedContact?.jid, selectedContact?.lid])

  // Effect: Carregar mensagens iniciais e configurar realtime
  useEffect(() => {
    if (!user?.account_id || !selectedContact || !supabase) {
      return
    }

    console.log('🔄 [useMessagesPaginated] Setting up for contact:', selectedContact.jid, 'ID:', selectedContact.id)

    // === LIMPEZA SÍNCRONA IMEDIATA ===
    // Limpar TODOS os estados relacionados a mensagens ANTES de qualquer operação assíncrona
    console.log('🧹 [useMessagesPaginated] Clearing all states synchronously')
    setMessages([])
    setError(null)
    setLoading(true)
    setHasMore(true)
    setIsLoadingMore(false)
    setSending(false)
    oldestMessageRef.current = null

    // Atualizar ref do contato atual
    currentContactRef.current = {
      id: selectedContact.id,
      jid: selectedContact.jid,
      lid: selectedContact.lid,
    }

    // Iniciar fetch assíncrono DEPOIS da limpeza síncrona
    fetchInitialMessages(selectedContact.id)

    const channelName = `messages-paginated-${user.account_id}-${selectedContact.jid.replace(/[^a-zA-Z0-9]/g, '-')}`
    const contactSnapshot = {
      id: selectedContact.id,
      jid: selectedContact.jid,
      lid: selectedContact.lid,
    }

    // Rede móvel, suspensão da aba e reconexões podem fazer o Realtime perder
    // um INSERT. Sincroniza apenas a janela recente e mescla sem apagar o histórico.
    const syncLatestMessages = async (): Promise<void> => {
      if (!supabase || document.hidden) return

      const filters = [
        `account_contact_id.eq.${contactSnapshot.id}`,
        `remote_jid.eq.${contactSnapshot.jid}`,
      ]
      if (contactSnapshot.lid) filters.push(`remote_jid.eq.${contactSnapshot.lid}`)

      const { data, error: syncError } = await supabase
        .from('account_messages')
        .select('*')
        .eq('account_id', user.account_id)
        .or(filters.join(','))
        .order('sent_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(RECENT_SYNC_LIMIT)

      if (syncError) {
        console.warn('⚠️ [useMessagesPaginated] Recent messages sync failed:', syncError.message)
        return
      }
      if (currentContactRef.current?.id !== contactSnapshot.id || !data?.length) return

      const recentMessages = [...data].reverse()
      setMessages(prev => {
        const next = [...prev]

        for (const dbMessage of recentMessages) {
          if (!messageBelongsToContact(dbMessage, contactSnapshot)) continue

          const existingIndex = next.findIndex(message =>
            message.id === dbMessage.id ||
            Boolean(dbMessage.wa_message_id && message.wa_message_id === dbMessage.wa_message_id)
          )
          const syncedMessage = {
            ...dbMessage,
            isOptimistic: false,
            isSending: false,
          }

          if (existingIndex >= 0) {
            next[existingIndex] = { ...next[existingIndex], ...syncedMessage }
          } else {
            next.push(syncedMessage)
          }
        }

        return next.sort((a, b) => {
          const timeDiff = new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
          return timeDiff || String(a.id).localeCompare(String(b.id))
        })
      })
    }

    console.log('📡 [useMessagesPaginated] Creating channel:', channelName)

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'account_messages',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          try {
            const newMessage = payload.new as AccountMessage
            const timestamp = new Date().toISOString()

            console.log(`📨 [useMessagesPaginated] ${timestamp} - INSERT event received:`, {
              id: newMessage.id,
              type: newMessage.msg_type,
              remote_jid: newMessage.remote_jid,
              from_me: newMessage.from_me,
              account_contact_id: newMessage.account_contact_id
            })

            if (!currentContactRef.current) {
              console.warn('⚠️ [useMessagesPaginated] No current contact ref, ignoring INSERT')
              return
            }

            if (!messageBelongsToContact(newMessage, currentContactRef.current)) {
              console.warn(`⚠️ [useMessagesPaginated] Message not for this contact - expected id/jid:`, currentContactRef.current, 'got:', {
                remote_jid: newMessage.remote_jid,
                account_contact_id: newMessage.account_contact_id,
              })
              return
            }

            console.log('✅ [useMessagesPaginated] INSERT passed validation, processing...')

            setMessages(prev => {
            const existsById = prev.some(msg => msg.id === newMessage.id)
            if (existsById) {
              return prev
            }

            if (newMessage.wa_message_id) {
              const byWaIdIdx = prev.findIndex(msg => msg.wa_message_id === newMessage.wa_message_id)
              if (byWaIdIdx >= 0) {
                console.log('🔄 [useMessagesPaginated] Replacing optimistic/existing by wa_message_id:', newMessage.wa_message_id)
                const next = [...prev]
                next[byWaIdIdx] = { ...newMessage, isOptimistic: false, isSending: false, sendSuccess: true }
                return next
              }
            }

            // Realtime arrived before convert-on-success: replace matching pending optimistic
            const pendingOptimisticIdx = prev.findIndex(msg =>
              Boolean(msg.isOptimistic || msg.isSending) &&
              msg.from_me === true &&
              msg.msg_type === newMessage.msg_type &&
              (msg.text_content || null) === (newMessage.text_content || null)
            )
            if (pendingOptimisticIdx >= 0) {
              console.log('🔄 [useMessagesPaginated] Replacing pending optimistic with realtime message')
              const next = [...prev]
              next[pendingOptimisticIdx] = { ...newMessage, isOptimistic: false, isSending: false, sendSuccess: true }
              return next
            }

            console.log(`✅ [useMessagesPaginated] Adding new message. Count: ${prev.length} -> ${prev.length + 1}`)
            return [...prev, { ...newMessage, isOptimistic: false }]
          })
          } catch (err) {
            console.error('❌ [useMessagesPaginated] Error processing INSERT event:', err)
            console.error('Stack trace:', err instanceof Error ? err.stack : 'No stack trace')
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'account_messages',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          try {
            const updatedMessage = payload.new as AccountMessage
            const timestamp = new Date().toISOString()

            console.log(`🔄 [useMessagesPaginated] ${timestamp} - UPDATE event received:`, {
              id: updatedMessage.id,
              type: updatedMessage.msg_type,
              remote_jid: updatedMessage.remote_jid,
              status: updatedMessage.status
            })

            if (!currentContactRef.current) {
              console.warn('⚠️ [useMessagesPaginated] No current contact ref, ignoring UPDATE')
              return
            }

            if (!messageBelongsToContact(updatedMessage, currentContactRef.current)) {
              console.warn(`⚠️ [useMessagesPaginated] UPDATE not for this contact`, currentContactRef.current, updatedMessage.remote_jid)
              return
            }

            console.log('✅ [useMessagesPaginated] UPDATE passed validation, processing...')

            setMessages(prev => {
            return prev.map(msg => {
              if (msg.id === updatedMessage.id) {
                return { ...updatedMessage, isOptimistic: false }
              }
              if (msg.wa_message_id === updatedMessage.wa_message_id &&
                  updatedMessage.wa_message_id &&
                  !String(msg.wa_message_id).startsWith('optimistic_')) {
                return { ...updatedMessage, isOptimistic: false }
              }
              return msg
            })
          })
          } catch (err) {
            console.error('❌ [useMessagesPaginated] Error processing UPDATE event:', err)
            console.error('Stack trace:', err instanceof Error ? err.stack : 'No stack trace')
          }
        }
      )
      .subscribe((status, err) => {
        const timestamp = new Date().toISOString()
        console.log(`📡 [useMessagesPaginated] ${timestamp} - Subscription status:`, status)

        if (status === 'SUBSCRIBED') {
          console.log('✅ [useMessagesPaginated] Successfully subscribed to channel:', channelName)
          if (supabase) {
            console.log('📊 [useMessagesPaginated] Active channels:', supabase.getChannels().length)
          }
        } else if (status === 'CLOSED') {
          console.warn('⚠️ [useMessagesPaginated] Channel CLOSED:', channelName)
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ [useMessagesPaginated] CHANNEL_ERROR:', { channelName, error: err })
          void syncLatestMessages()
        } else if (status === 'TIMED_OUT') {
          console.error('⏱️ [useMessagesPaginated] Subscription TIMED_OUT:', channelName)
          void syncLatestMessages()
        }
      })

    const syncTimeout = window.setTimeout(() => void syncLatestMessages(), 1500)
    const syncInterval = window.setInterval(() => void syncLatestMessages(), RECENT_SYNC_INTERVAL_MS)
    const handleVisibilityChange = () => {
      if (!document.hidden) void syncLatestMessages()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      console.log('🔌 [useMessagesPaginated] Cleanup for contact:', currentContactRef.current?.jid)
      window.clearTimeout(syncTimeout)
      window.clearInterval(syncInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      // Cancelar fetch em andamento
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }

      // Limpar ref do contato atual
      currentContactRef.current = null

      // Remover canal realtime
      if (supabase && channel) {
        supabase.removeChannel(channel)
      }
    }
  }, [user?.account_id, selectedContact?.id, selectedContact?.jid, selectedContact?.lid])

  return {
    messages,
    loading,
    error,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
    sendMessage,
    sendAudio,
    sendImage,
    sendVideo,
    sendDocument,
    refetch,
    sending,
  }
}
